import { setEmailCategory } from "./graphClient";

/**
 * A durable, background queue for applying Outlook categories to emails.
 *
 * The queue is persisted to localStorage after every mutation so that pending
 * work survives task pane reloads. On startup, call {@link resumeCategoryQueue}
 * to pick up any items that were left unprocessed.
 */

interface CategoryQueueItem {
    emailId: string;
    categoryName: string;
    attempts: number;
}

const QUEUE_STORAGE_KEY = "efile-category-queue";
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 5000;

let processing = false;
let retryTimer: number | null = null;

function isValidItem(item: unknown): item is CategoryQueueItem {
    return (
        typeof item === "object" &&
        item !== null &&
        typeof (item as CategoryQueueItem).emailId === "string" &&
        typeof (item as CategoryQueueItem).categoryName === "string" &&
        typeof (item as CategoryQueueItem).attempts === "number"
    );
}

function loadQueue(): CategoryQueueItem[] {
    try {
        const saved = localStorage.getItem(QUEUE_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
                return parsed.filter(isValidItem);
            }
        }
    } catch (error) {
        console.warn("Failed to load category queue:", error);
    }
    return [];
}

function saveQueue(queue: CategoryQueueItem[]): void {
    try {
        if (queue.length === 0) {
            localStorage.removeItem(QUEUE_STORAGE_KEY);
        } else {
            localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
        }
    } catch (error) {
        console.warn("Failed to persist category queue:", error);
    }
}

/**
 * Queues a category update to run in the background and kicks off processing.
 * Returns immediately after durably persisting the item, so callers do not
 * have to wait on the Graph API.
 */
export function enqueueCategoryUpdate(emailId: string, categoryName: string): void {
    const queue = loadQueue();
    // Collapse any pending update for the same email; only the latest matters.
    const filtered = queue.filter((item) => item.emailId !== emailId);
    filtered.push({ emailId, categoryName, attempts: 0 });
    saveQueue(filtered);
    void processCategoryQueue();
}

/**
 * Drains the persisted queue, applying each category update via the Graph API.
 * Safe to call repeatedly and concurrently; only one drain runs at a time.
 * On a transient failure, the item is kept and a retry is scheduled with a
 * backoff so the work is not lost.
 */
export async function processCategoryQueue(): Promise<void> {
    if (processing) {
        return;
    }
    processing = true;

    let scheduleRetry = false;
    try {
        while (true) {
            const queue = loadQueue();
            if (queue.length === 0) {
                break;
            }

            const item = queue[0];
            const success = await setEmailCategory(item.emailId, item.categoryName);

            // Reload after the awaited call so newly enqueued items (which may
            // have replaced this one) are respected.
            const current = loadQueue();
            const idx = current.findIndex(
                (q) => q.emailId === item.emailId && q.categoryName === item.categoryName
            );

            if (idx === -1) {
                // The item was superseded or already removed; move on.
                continue;
            }

            if (success) {
                current.splice(idx, 1);
                saveQueue(current);
                continue;
            }

            // Transient failure: retry later, or drop after too many attempts.
            current[idx].attempts += 1;
            if (current[idx].attempts >= MAX_ATTEMPTS) {
                console.warn(
                    `Giving up on category update for email ${item.emailId} after ${MAX_ATTEMPTS} attempts.`
                );
                current.splice(idx, 1);
                saveQueue(current);
                continue;
            }

            saveQueue(current);
            scheduleRetry = true;
            break;
        }
    } finally {
        processing = false;
    }

    if (scheduleRetry && retryTimer === null) {
        retryTimer = (globalThis.setTimeout ?? setTimeout)(() => {
            retryTimer = null;
            void processCategoryQueue();
        }, RETRY_DELAY_MS) as unknown as number;
    }
}

/**
 * Resumes any category updates that were left pending from a previous session.
 * Call this once during app startup.
 */
export function resumeCategoryQueue(): void {
    void processCategoryQueue();
}
